import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Clock, Gift, KeyRound, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { EXPERIENCES, BESOINS, DUREES, LIEUX_FILTRE, getExperience } from "@/data/experiences";
import { DemandeDialog } from "@/components/site/DemandeBon";
import { EmailGate, getMailAcces } from "@/components/site/EmailGate";
import { CONTACT } from "@/data/content";
import { api, formatApiError } from "@/lib/api";

const CODE_KEY = "tpoz_code";
const telHref = (tel) => `tel:${(tel || "").replace(/[^+\d]/g, "")}`;

const Chip = ({ active, onClick, testid, children }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors text-left ${
      active
        ? "bg-[#C6870F] border-[#C6870F] text-white"
        : "bg-white border-[#2E2A22]/15 text-[#2E2A22] hover:border-[#C6870F] hover:text-[#A66E08]"
    }`}
  >
    {children}
  </button>
);

// NIVEAU 1 : carte expérience — titre, photo, description, avantage. Rien d'autre.
const ExperienceCard = ({ exp, onOpen }) => (
  <button
    onClick={() => onOpen(exp)}
    data-testid={`exp-card-${exp.id}`}
    className="flex flex-col text-left overflow-hidden rounded-2xl bg-white border border-[#2E2A22]/8 transition-all hover:-translate-y-1 hover:shadow-[0_12px_30px_rgba(45,40,48,0.1)]"
  >
    <img src={exp.photo} alt={exp.nom} loading="lazy" className="w-full aspect-[4/3] object-cover" />
    <div className="flex flex-col flex-1 p-5">
      {exp.statut && (
        <span className="self-start mb-1.5 px-2.5 py-0.5 rounded-full bg-[#EBD9AE]/60 text-[11px] font-semibold uppercase tracking-wider text-[#A66E08]">
          {exp.statut}
        </span>
      )}
      <h3 className="font-serif text-xl leading-snug">{exp.nom}</h3>
      <p className="mt-2 flex items-start gap-1.5 text-sm text-[#6B6355]" data-testid={`exp-commune-${exp.id}`}>
        <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#A66E08]" /> {exp.lieuxLabel}
      </p>
      <div className="flex-1" />
      <p className="mt-3 inline-block self-start rounded-lg bg-[#A66E08]/10 px-3 py-1.5 text-xs font-semibold text-[#A66E08]" data-testid={`exp-avantage-${exp.id}`}>
        Avantage T'POZ : {exp.avantage}
      </p>
    </div>
  </button>
);

const TarifBloc = ({ exp, o, i }) => (
  <div className="mt-3 space-y-1.5" data-testid={`lieu-tarifs-${exp.id}-${i}`}>
    {o.formules ? (
      o.formules.map((f) => (
        <p key={f.label} className="text-sm text-[#2E2A22]">
          <span className="font-semibold">{f.label} :</span> à partir de{" "}
          <span className="text-[#6B6355] line-through">{f.public}</span>{" "}
          <span className="text-base font-bold text-[#A66E08]">→ {f.tpoz}</span>
        </p>
      ))
    ) : (
      <>
        <p className="text-xs text-[#6B6355]" data-testid={`lieu-tarif-${exp.id}-${i}`}>
          {/^\d/.test(o.tarif_public || "") ? (
            <>
              Tarif public : à partir de{" "}
              <span className={o.tarif_tpoz ? "line-through" : "font-semibold"}>{o.tarif_public}</span>
            </>
          ) : (
            <>Tarif public : communiqué par la partenaire</>
          )}
        </p>
        {o.tarif_tpoz && (
          <p className="text-base font-bold text-[#A66E08]" data-testid={`lieu-tarif-tpoz-${exp.id}-${i}`}>
            Tarif T'POZ : {/^\d/.test(o.tarif_tpoz) ? `à partir de ${o.tarif_tpoz}` : o.tarif_tpoz}
          </p>
        )}
      </>
    )}
    <p className="inline-block rounded-lg bg-[#A66E08]/10 px-3 py-1 text-sm font-semibold text-[#A66E08]" data-testid={`lieu-avantage-${exp.id}-${i}`}>
      Avantage T'POZ : {o.avantage}
    </p>
  </div>
);

// NIVEAU 2 : encart lieu/partenaire — sans coordonnées. NIVEAU 3 : coordonnées si code saisi.
const LieuEncart = ({ exp, o, i, codeOk, complet, onDemande }) => {
  return (
    <div data-testid={`lieu-encart-${exp.id}-${i}`} className="rounded-2xl bg-white border border-[#2E2A22]/8 p-5 sm:p-6">
      <div className="flex items-start gap-4">
        {o.photoP && (
          <img src={o.photoP} alt={o.partenaire} loading="lazy" className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover shrink-0" />
        )}
        <div className="min-w-0">
          <h4 className="font-semibold text-[#2E2A22] leading-snug">{o.partenaire}</h4>
          <p className="mt-0.5 flex items-center gap-1.5 text-sm text-[#6B6355]">
            <MapPin className="h-3.5 w-3.5 text-[#A66E08]" /> {o.lieu}
          </p>
          <p className="mt-0.5 flex items-center gap-1.5 text-sm text-[#6B6355]">
            <Clock className="h-3.5 w-3.5 text-[#A66E08]" /> {o.duree}
          </p>
        </div>
      </div>
      <p className="mt-3 text-sm text-[#6B6355] leading-relaxed">{o.offre}</p>
      <TarifBloc exp={exp} o={o} i={i} />
      {o.delai_reservation && (
        <p className="mt-2 text-sm text-[#6B6355]" data-testid={`lieu-delai-${exp.id}-${i}`}>
          <span className="font-semibold text-[#2E2A22]">Délai de réservation :</span> {o.delai_reservation}
        </p>
      )}
      <p className="mt-2 text-sm text-[#6B6355]">
        Disponibilités : vous les obtiendrez auprès du partenaire avec votre code T'POZ.
      </p>
      {codeOk && o.tel && (
        <div className="mt-3 rounded-xl bg-[#2E2A22] p-4 text-sm text-[#FBF7EF]" data-testid={`lieu-contact-${exp.id}-${i}`}>
          <p className="font-semibold">Réservez directement avec votre code :</p>
          <a href={telHref(o.tel)} className="mt-1 inline-flex items-center gap-2 font-semibold text-[#EBD9AE] underline underline-offset-4">
            <Phone className="h-4 w-4" /> {o.tel}
          </a>
        </div>
      )}
      {complet && (
        <p data-testid={`lieu-complet-${exp.id}-${i}`} className="mt-3 rounded-xl bg-[#F1E8D8] px-4 py-3 text-sm font-medium text-[#8a5a1e]">
          Cette expérience est complète ce mois-ci. Elle sera de nouveau disponible le 1er du mois prochain.
        </p>
      )}
      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <button
          onClick={() => onDemande({ offre: `${exp.nom} (${o.partenaire}, ${o.lieu})`, partenaire: o.partenaire, partenaire_tel: o.tel || "", avantage: o.avantage || exp.avantage || "", delai_reservation: o.delai_reservation || "" })}
          disabled={complet}
          data-testid={`lieu-obtenir-${exp.id}-${i}`}
          className="px-5 py-3 rounded-full bg-[#C6870F] text-white text-sm font-semibold hover:bg-[#A66E08] transition-colors text-center disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#C6870F]"
        >
          Obtenir cette T'POZ
        </button>
        <Link
          to={`/offrir?offre=${encodeURIComponent(`${exp.nom} (${o.partenaire}, ${o.lieu})`)}&partenaire=${encodeURIComponent(o.partenaire)}&tel=${encodeURIComponent(o.tel || "")}&avantage=${encodeURIComponent(o.avantage || exp.avantage || "")}&delai=${encodeURIComponent(o.delai_reservation || "")}`}
          data-testid={`lieu-offrir-${exp.id}-${i}`}
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[#C6870F]/50 text-[#A66E08] text-sm font-semibold hover:bg-[#C6870F]/10 transition-colors"
        >
          <Gift className="h-4 w-4" /> Offrir cette T'POZ
        </Link>
      </div>
    </div>
  );
};

const CodeUnlock = ({ codeOk, onUnlocked }) => {
  const [code, setCode] = useState("");
  const [checking, setChecking] = useState(false);
  if (codeOk) {
    return (
      <p data-testid="code-unlocked" className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm font-medium text-emerald-700">
        Code vérifié : les coordonnées des partenaires sont affichées ci-dessous.
      </p>
    );
  }
  const verifier = async () => {
    setChecking(true);
    try {
      await api.post("/codes/verifier", { code });
      localStorage.setItem(CODE_KEY, code.trim().toUpperCase());
      onUnlocked();
      toast.success("Code vérifié !");
    } catch (err) {
      toast.error("Code non reconnu", {
        description: formatApiError(err.response?.data?.detail) || err.message,
      });
    } finally {
      setChecking(false);
    }
  };
  return (
    <div data-testid="code-unlock" className="rounded-2xl border border-dashed border-[#A66E08]/40 bg-white/70 p-4 sm:p-5">
      <p className="flex items-center gap-2 text-sm font-semibold text-[#2E2A22]">
        <KeyRound className="h-4 w-4 text-[#A66E08]" /> Vous avez déjà votre code T'POZ ?
      </p>
      <p className="mt-1 text-xs text-[#6B6355]">Saisissez-le pour afficher les coordonnées des partenaires et réserver directement.</p>
      <div className="mt-3 flex flex-col sm:flex-row gap-2">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          data-testid="code-unlock-input"
          placeholder="TPZ-2026-0001"
          className="flex-1 rounded-full border border-[#2E2A22]/15 bg-white px-4 py-2.5 text-sm uppercase focus:outline-none focus:border-[#C6870F]"
        />
        <button
          onClick={verifier}
          disabled={checking || !code.trim()}
          data-testid="code-unlock-submit"
          className="px-5 py-2.5 rounded-full bg-[#2E2A22] text-[#FBF7EF] text-sm font-semibold hover:bg-[#443E33] transition-colors disabled:opacity-50"
        >
          {checking ? "Vérification…" : "Vérifier mon code"}
        </button>
      </div>
    </div>
  );
};

export default function ExperiencesPage() {
  const [params, setParams] = useSearchParams();
  const besoin = params.get("besoin") || "";
  const duree = params.get("duree") || "";
  const lieu = params.get("lieu") || "";
  const expId = params.get("exp") || "";
  const [demande, setDemande] = useState(null);
  const [mailAcces, setMailAcces] = useState(getMailAcces());
  const [gateFor, setGateFor] = useState(null);
  const [codeOk, setCodeOk] = useState(() => Boolean(localStorage.getItem(CODE_KEY)));
  const [complets, setComplets] = useState({});

  const detail = expId ? getExperience(expId) : null;

  useEffect(() => {
    api.get("/plafonds/etat").then((r) => setComplets(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [expId]);

  // Lien direct /tpoz?exp=… : la fiche reste réservée aux visiteuses ayant laissé leur e-mail.
  useEffect(() => {
    if (detail && !mailAcces) setGateFor(detail);
  }, [detail, mailAcces]);

  const setParam = (key, value) => {
    const p = new URLSearchParams(params);
    if (value) p.set(key, value);
    else p.delete(key);
    setParams(p, { replace: true });
  };

  const openExp = (exp) => {
    if (!mailAcces) {
      setGateFor(exp);
      return;
    }
    api.post("/acces", { email: mailAcces.email, experience: exp.nom }).catch(() => {});
    setParam("exp", exp.id);
  };

  const matchExp = (e, besoinKey) =>
    e.rubriques.includes(besoinKey) &&
    (!duree || e.dureeCat === duree) &&
    (!lieu ||
      (e.offres.length
        ? e.offres.some((o) => (o.secteurs || e.secteurs).includes(lieu))
        : e.secteurs.includes(lieu)));

  const sections = useMemo(() => {
    const list = besoin ? BESOINS.filter((b) => b.key === besoin) : BESOINS;
    return list
      .map((b) => ({ besoin: b, exps: EXPERIENCES.filter((e) => matchExp(e, b.key)) }))
      .filter((s) => s.exps.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [besoin, duree, lieu]);

  const total = sections.reduce((n, s) => n + s.exps.length, 0);
  const besoinLabel = detail ? BESOINS.find((b) => detail.rubriques.includes(b.key))?.label || "" : "";

  return (
    <div data-testid="experiences-page" className="min-h-screen bg-[#FBF7EF] text-[#2E2A22]" style={{ fontFamily: "Manrope, sans-serif" }}>
      <header className="sticky top-0 z-40 bg-[#FBF7EF] border-b border-[#2E2A22]/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" data-testid="exp-header-logo">
            <img src="/logo-tpoz.png" alt="T'POZ — Répit & Ressourcement" className="h-14 sm:h-20 w-auto" />
          </Link>
          <button
            onClick={() => setDemande({ offre: "", besoin: "" })}
            data-testid="exp-header-cta"
            className="px-4 sm:px-5 py-2 sm:py-2.5 rounded-full bg-[#C6870F] text-white text-xs sm:text-sm font-semibold hover:bg-[#A66E08] transition-colors"
          >
            Demander ma T'POZ
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 md:py-14">
        {detail && mailAcces ? (
          /* ——— NIVEAU 2 : fiche détaillée ——— */
          <div data-testid="exp-detail">
            <button
              onClick={() => setParam("exp", "")}
              data-testid="exp-detail-back"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#A66E08] hover:text-[#C6870F] transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> Toutes les T'POZ
            </button>
            <div className="mt-5 overflow-hidden rounded-3xl">
              <img src={detail.photo} alt={detail.nom} className="w-full aspect-[16/8] object-cover" />
            </div>
            <h1 data-testid="exp-detail-title" className="mt-6 font-serif text-3xl sm:text-4xl leading-tight">{detail.nom}</h1>
            <p className="mt-2 font-serif text-lg italic text-[#6B6355]">{detail.accroche}</p>
            <p className="mt-4 text-sm text-[#2E2A22]/80 leading-relaxed" data-testid="exp-detail-comprend">{detail.comprend}</p>

            <div className="mt-6">
              <CodeUnlock codeOk={codeOk} onUnlocked={() => setCodeOk(true)} />
            </div>

            {detail.offres.length > 0 ? (
              <div className="mt-8">
                <h2 className="font-serif text-2xl">
                  Les lieux où vivre cette <em className="text-[#A66E08]">T'POZ</em>
                </h2>
                <div className="mt-5 space-y-5">
                  {detail.offres.map((o, i) => (
                    <LieuEncart
                      key={i}
                      exp={detail}
                      o={o}
                      i={i}
                      codeOk={codeOk}
                      complet={Boolean(complets[o.partenaire])}
                      onDemande={(d) => setDemande({ ...d, besoin: besoinLabel })}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="mt-8 rounded-2xl bg-white border border-dashed border-[#A66E08]/40 p-6 text-sm" data-testid="exp-detail-concue">
                <p className="text-[#6B6355]">
                  Une T'POZ conçue par l'association {CONTACT.association}. Le programme détaillé sera
                  bientôt dévoilé.
                </p>
                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => setDemande({ offre: detail.nom, besoin: besoinLabel })}
                    data-testid={`lieu-obtenir-${detail.id}`}
                    className="px-5 py-3 rounded-full bg-[#C6870F] text-white text-sm font-semibold hover:bg-[#A66E08] transition-colors"
                  >
                    Obtenir cette T'POZ
                  </button>
                  <Link
                    to="/offrir"
                    data-testid={`lieu-offrir-${detail.id}`}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[#C6870F]/50 text-[#A66E08] text-sm font-semibold hover:bg-[#C6870F]/10 transition-colors"
                  >
                    <Gift className="h-4 w-4" /> Offrir cette T'POZ
                  </Link>
                </div>
              </div>
            )}

            <p className="mt-8 text-sm text-[#6B6355]" data-testid="exp-detail-appel">
              Vous préférez appeler ?{" "}
              <a href={CONTACT.phoneHref} className="font-semibold text-[#A66E08] underline underline-offset-4">{CONTACT.phone}</a>
            </p>
          </div>
        ) : (
          /* ——— NIVEAU 1 : accès libre ——— */
          <>
            <h1 data-testid="exp-page-title" className="font-serif text-4xl sm:text-5xl leading-[1.1]">
              Les <em className="text-[#A66E08]">T'POZ</em>
            </h1>
            <p className="mt-4 max-w-2xl text-sm text-[#6B6355] leading-relaxed">
              Choisissez votre besoin, puis le temps dont vous disposez et le lieu : seules les
              T'POZ qui vous correspondent s'affichent.
            </p>

            <div className="mt-8">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#A66E08]">1 · Je choisis mon besoin</p>
              <div className="mt-3 flex flex-wrap gap-2.5" data-testid="exp-filtre-besoin">
                {BESOINS.map((b) => (
                  <Chip key={b.key} testid={`exp-besoin-${b.key}`} active={besoin === b.key} onClick={() => setParam("besoin", besoin === b.key ? "" : b.key)}>
                    {b.label}
                  </Chip>
                ))}
              </div>

              {besoin && (
                <div className="mt-6 space-y-4" data-testid="exp-filtres-avances">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#A66E08]">2 · Le temps dont je dispose <span className="normal-case tracking-normal text-[#6B6355]/70 font-normal">(facultatif)</span></p>
                    <div className="mt-2 flex flex-wrap gap-2.5" data-testid="exp-filtre-duree">
                      {DUREES.map((d) => (
                        <Chip key={d.key} testid={`exp-duree-${d.key}`} active={duree === d.key} onClick={() => setParam("duree", duree === d.key ? "" : d.key)}>
                          {d.label}
                        </Chip>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#A66E08]">3 · Je peux préciser le lieu <span className="normal-case tracking-normal text-[#6B6355]/70 font-normal">(facultatif)</span></p>
                    <div className="mt-2 flex flex-wrap gap-2.5" data-testid="exp-filtre-lieu">
                      {LIEUX_FILTRE.map((l) => (
                        <Chip key={l.key} testid={`exp-lieu-${l.key}`} active={lieu === l.key} onClick={() => setParam("lieu", lieu === l.key ? "" : l.key)}>
                          {l.label}
                        </Chip>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <p className="mt-5">
                <Link to="/carte" data-testid="exp-lien-carte" className="inline-flex items-center gap-2 text-sm font-semibold text-[#A66E08] underline underline-offset-4 hover:text-[#C6870F] transition-colors">
                  <MapPin className="h-4 w-4" /> Voir la carte des activités
                </Link>
              </p>
            </div>

            {!besoin ? (
              <div className="mt-10 rounded-3xl border border-dashed border-[#A66E08]/40 bg-white/60 p-8 text-center" data-testid="exp-choisir-besoin">
                <p className="font-serif text-xl text-[#2E2A22]">Et vous, de quoi avez-vous besoin en ce moment ?</p>
                <p className="mt-2 text-sm text-[#6B6355]">
                  Choisissez un besoin ci-dessus pour découvrir les T'POZ qui vous correspondent.
                </p>
              </div>
            ) : total === 0 ? (
              <p className="mt-10 text-sm text-[#6B6355]" data-testid="exp-results-empty">
                Aucune T'POZ ne correspond à ces critères pour le moment. Essayez d'élargir la durée ou le lieu.
              </p>
            ) : (
              <div className="mt-10 space-y-12" data-testid="exp-results">
                {sections.map(({ besoin: b, exps }) => (
                  <section key={b.key} data-testid={`exp-section-${b.key}`}>
                    <h2 className="font-serif text-2xl leading-snug">{b.label}</h2>
                    <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {exps.map((e) => (
                        <ExperienceCard key={`${b.key}-${e.id}`} exp={e} onOpen={openExp} />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}

            <div className="mt-10 rounded-2xl border border-[#2E2A22]/8 bg-white p-6" data-testid="exp-assoc-encart">
              <h2 className="font-serif text-xl">Les T'POZ de l'association</h2>
              <p className="mt-1 text-sm text-[#6B6355]">À venir.</p>
            </div>
          </>
        )}

        <div className="mt-14 rounded-3xl bg-[#2E2A22] text-[#FBF7EF] px-8 py-10 md:px-12 flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <h3 className="font-serif text-2xl">Vous avez trouvé votre T'POZ ?</h3>
            <p className="mt-2 text-sm text-[#FBF7EF]/70">Demandez votre code en quelques minutes, puis réservez directement auprès du partenaire.</p>
            <p className="mt-2 text-sm text-[#FBF7EF]/70">
              Vous préférez appeler ?{" "}
              <a href={CONTACT.phoneHref} data-testid="exp-footer-appel" className="font-semibold text-[#EBD9AE] underline underline-offset-4">{CONTACT.phone}</a>
            </p>
          </div>
          <button
            onClick={() => setDemande({ offre: "", besoin: "" })}
            data-testid="exp-footer-cta"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#C6870F] text-white text-sm font-semibold hover:bg-[#D9A05B] transition-colors shrink-0"
          >
            Demander ma T'POZ
          </button>
        </div>
      </main>

      <EmailGate
        open={Boolean(gateFor)}
        onOpenChange={(v) => {
          if (!v) {
            setGateFor(null);
            if (!getMailAcces() && expId) setParam("exp", "");
          }
        }}
        experience={gateFor?.nom || ""}
        onGranted={(v) => {
          setMailAcces(v);
          const exp = gateFor;
          setGateFor(null);
          if (exp) setParam("exp", exp.id);
        }}
      />
      <DemandeDialog
        open={Boolean(demande)}
        onOpenChange={(v) => !v && setDemande(null)}
        offre={demande?.offre || ""}
        besoin={demande?.besoin || ""}
        partenaire={demande?.partenaire || ""}
        partenaireTel={demande?.partenaire_tel || ""}
        avantage={demande?.avantage || ""}
        delaiReservation={demande?.delai_reservation || ""}
      />
    </div>
  );
}
